/**
 * Auto Assignment Dashboard
 * Real-time monitoring and control for Team Leaders and Admins
 * 
 * Features:
 * - Queue statistics (pending, assigned today, in progress)
 * - Processor workload overview with utilization bars
 * - Stuck processor alerts
 * - Manual trigger for auto-assignment
 * - Recent assignment activity feed
 * - Queue order list with quick actions
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import {
  LayoutDashboard,
  Users,
  Package,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  RefreshCw,
  Unlock,
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  Activity,
  Zap,
  Eye,
  UserPlus
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import ProcessorWorkloadTable from './ProcessorWorkloadTable';
import BulkSetStatesModal from './BulkSetStatesModal';
import ConfigAuditModal from './ConfigAuditModal';
import { ClipboardCheck } from 'lucide-react';
import { buildAutoAssignToast } from '../../lib/autoAssignToast';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AutoAssignmentDashboard = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const userRole = auth?.user?.role || '';

  // Phase A.6 — team tab state. Initial team derived from user's role.
  // Title TL lands on Search; everyone else (Admin, Tax TL) starts on Tax.
  const initialTeam = useMemo(
    () => (userRole === 'title_search_team_leader' ? 'search' : 'tax'),
    [userRole]
  );
  const [team, setTeam] = useState(initialTeam);
  const teamLabel = team === 'search' ? 'Search Team' : 'Tax Team';

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggeringAssignment, setTriggeringAssignment] = useState(false);
  const [bulkStatesOpen, setBulkStatesOpen] = useState(false);
  const [configAuditOpen, setConfigAuditOpen] = useState(false);
  const [bulkStatesProcessors, setBulkStatesProcessors] = useState([]);
  const canBulkEdit = ['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'].includes(userRole);
  // Phase A.6 polish (Jun 2026) — imperative handle on the new workload
  // table so System Health "View stuck" jumps straight to a pre-filtered view.
  const workloadTableRef = useRef(null);
  const workloadSectionRef = useRef(null);

  const handleViewStuckProcessors = useCallback(() => {
    workloadTableRef.current?.applyStuckFilter?.();
    if (workloadSectionRef.current) {
      workloadSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const fetchDashboardData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const summaryRes = await axios.get(`${API}/api/auto-assignment/dashboard/summary?team=${team}`);
      setSummary(summaryRes.data);
      
      if (showToast) {
        toast.success('Dashboard refreshed');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [team]);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchDashboardData(), 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleTriggerAssignment = async () => {
    try {
      setTriggeringAssignment(true);
      // AUTOASSIGN-ASYNC-001 — the trigger returns 202 + run_id immediately
      // (the engine runs in the background; long runs previously hit the
      // Cloudflare ~100s limit and surfaced 5xx to the user). Poll the run
      // status until it completes, then toast the real outcome.
      const response = await axios.post(`${API}/api/auto-assignment/auto-assign/${team}/run`);
      const runId = response.data?.run_id;
      toast.info('Auto-assignment run started…', { description: 'Running in the background — results will appear here shortly.' });

      const POLL_MS = 3000;
      const MAX_POLLS = 100; // ~5 minutes
      let run = null;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_MS));
        const res = await axios.get(`${API}/api/auto-assignment/auto-assign/runs/${runId}`);
        run = res.data;
        if (run?.status !== 'running') break;
      }

      if (run?.status === 'completed') {
        // Mar 2026 — UAT-AUTO-ASSIGN-MISLEADING-SUCCESS. Use the shared
        // decision helper so the toast reflects the actual outcome —
        // see /app/frontend/src/lib/autoAssignToast.js
        const decision = buildAutoAssignToast({ ...run, team }, summary);
        const opts = decision.description ? { description: decision.description } : undefined;
        const surface = toast[decision.kind] || toast.message;
        surface(decision.message, opts);
      } else if (run?.status === 'failed') {
        toast.error('Auto-assignment run failed', { description: (run.errors || []).join('; ') || 'See run history for details.' });
      } else {
        toast.info('Auto-assignment is still running', { description: 'Check the run history below in a few minutes for the result.' });
      }

      fetchDashboardData();
    } catch (err) {
      console.error('Error triggering assignment:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      if (err?.response?.status === 409) {
        toast.warning('Run already in progress', { description: detail });
      } else {
        toast.error(`Failed to trigger auto-assignment: ${detail}`);
      }
    } finally {
      setTriggeringAssignment(false);
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return 'bg-red-500';
    if (utilization >= 70) return 'bg-orange-500';
    if (utilization >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getHealthStatusColor = (isHealthy) => {
    return isHealthy ? 'text-green-600' : 'text-orange-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="auto-assignment-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mr-2"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <LayoutDashboard className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auto Assignment Dashboard</h1>
            <p className="text-sm text-gray-500">{teamLabel} Queue Management</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Phase A.6 — team tabs. Admin sees both; TLs see only their team's tab as active. */}
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" data-testid="team-tabs">
            <button
              type="button"
              onClick={() => setTeam('tax')}
              disabled={userRole === 'title_search_team_leader'}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                team === 'tax'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
              data-testid="team-tab-tax"
            >
              Tax Team
            </button>
            <button
              type="button"
              onClick={() => setTeam('search')}
              disabled={userRole === 'tax_search_team_leader'}
              className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition-colors ${
                team === 'search'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
              data-testid="team-tab-search"
            >
              Search Team
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfigAuditOpen(true)}
            data-testid="config-audit-btn"
          >
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Config Audit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDashboardData(true)}
            disabled={refreshing}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleTriggerAssignment}
            disabled={triggeringAssignment}
            className={team === 'search' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="trigger-assignment-btn"
          >
            {triggeringAssignment ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Auto-Assign for {teamLabel}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Pending Orders */}
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Queue</p>
                <p className="text-2xl font-bold text-orange-600">{summary?.queue?.pending_orders || 0}</p>
              </div>
              <Package className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Awaiting assignment</p>
          </CardContent>
        </Card>

        {/* Assigned Today */}
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Assigned Today</p>
                <p className="text-2xl font-bold text-green-600">{summary?.queue?.assigned_today || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Orders distributed</p>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{summary?.queue?.in_progress || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Currently being worked</p>
          </CardContent>
        </Card>

        {/* Total Capacity */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Available Capacity</p>
                <p className="text-2xl font-bold text-purple-600">{summary?.capacity?.total_capacity || 0}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {summary?.capacity?.enabled_processors || 0}/{summary?.capacity?.total_processors || 0} processors enabled
            </p>
          </CardContent>
        </Card>

        {/* Health Status */}
        <Card className={`border-l-4 ${summary?.health?.is_healthy ? 'border-l-green-500' : 'border-l-red-500'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">System Health</p>
                <p className={`text-lg font-bold ${getHealthStatusColor(summary?.health?.is_healthy)}`}>
                  {summary?.health?.is_healthy ? 'Healthy' : 'Needs Attention'}
                </p>
              </div>
              {summary?.health?.is_healthy ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              )}
            </div>
            {summary?.health?.stuck_processor_count > 0 && (
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-red-500">
                  {summary.health.stuck_processor_count} processor(s) stuck
                </p>
                <button
                  type="button"
                  onClick={handleViewStuckProcessors}
                  className="text-xs font-medium text-blue-600 underline hover:text-blue-800"
                  data-testid="system-health-view-stuck"
                >
                  View →
                </button>
              </div>
            )}
            {summary?.health?.inactive_processor_count > 0 && (
              <p className="text-xs text-orange-500 mt-1">
                {summary.health.inactive_processor_count} processor(s) not active
              </p>
            )}
            {summary?.health?.missing_states_processor_count > 0 && (
              <p className="text-xs text-red-500 mt-1 font-medium" data-testid="system-health-missing-states">
                {summary.health.missing_states_processor_count} enabled processor(s) have no eligible states
              </p>
            )}
            {summary?.health?.over_capacity_processor_count > 0 && (
              <p className="text-xs text-orange-500 mt-1" data-testid="system-health-over-capacity">
                {summary.health.over_capacity_processor_count} processor(s) over WIP capacity
              </p>
            )}
            <p
              className={`text-xs mt-2 font-semibold ${
                (summary?.health?.usable_processor_count || 0) === 0 ? 'text-red-600' : 'text-green-600'
              }`}
              data-testid="system-health-usable-count"
            >
              Engine-usable processors: {summary?.health?.usable_processor_count ?? 0}
              {(summary?.health?.usable_processor_count || 0) === 0 &&
                ' — auto-assignment cannot assign any orders'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Processor Workloads — full width (Recent Activity panel removed
          Aug 2026 per Title Flow UI consistency request; assignment history
          remains available via the audit trail / run history). */}
      <div ref={workloadSectionRef}>
        <ProcessorWorkloadTable
          ref={workloadTableRef}
          processors={summary?.processors || []}
          capacity={summary?.capacity || {}}
          canBulkEdit={canBulkEdit}
          onBulkSetStates={(procs) => {
            setBulkStatesProcessors(procs);
            setBulkStatesOpen(true);
          }}
        />
      </div>

      {/* Order Queue panel removed Aug 2026 (duplicated the operational
          workspaces). */}

      {/* Last Run Info — Jun 2026: exclusion transparency breakdown.
          Restored Aug 2026: intentionally shown BOTH here (for Team
          Leaders) and on the Admin Dashboard (ADMIN-RUN-MOVE-001). */}
      {summary?.last_run && (
        <Card data-testid="last-run-breakdown">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
              <Zap className="h-4 w-4 text-blue-500" />
              <span className="font-medium text-gray-900">Last auto-assignment run:</span>
              {summary.last_run.status === 'running' ? (
                <span className="text-blue-600 font-medium">In progress…</span>
              ) : (
                <>
                  <span className={summary.last_run.success ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                    {summary.last_run.success ? 'Success' : 'Failed'}
                  </span>
                  <span>•</span>
                  <span>{summary.last_run.orders_assigned}/{summary.last_run.orders_processed} orders assigned</span>
                </>
              )}
              {summary.last_run.trigger && (
                <>
                  <span>•</span>
                  <span className="capitalize">{summary.last_run.trigger}</span>
                </>
              )}
              {summary.last_run.run_at && (
                <>
                  <span>•</span>
                  <span>{formatDistanceToNow(new Date(summary.last_run.run_at), { addSuffix: true })}</span>
                </>
              )}
            </div>
            {summary.last_run.diagnostics && typeof summary.last_run.diagnostics.total_unassigned === 'number' && (
              <div className="mt-2 text-sm text-gray-600" data-testid="last-run-diagnostics">
                <p>
                  <span className="font-medium">{summary.last_run.diagnostics.total_unassigned}</span>
                  {' '}unassigned order(s) system-wide •{' '}
                  <span className="font-medium">{summary.last_run.diagnostics.eligible_orders}</span>
                  {' '}eligible for {teamLabel}
                </p>
                {Array.isArray(summary.last_run.diagnostics.exclusion_reasons) &&
                  summary.last_run.diagnostics.exclusion_reasons.length > 0 && (
                  <ul className="mt-1 space-y-0.5" data-testid="last-run-exclusion-reasons">
                    {summary.last_run.diagnostics.exclusion_reasons.map((r) => (
                      <li key={r.code} className="flex items-start gap-1.5 text-xs text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          <span className="font-semibold">{r.count} excluded</span> — {r.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <BulkSetStatesModal
        isOpen={bulkStatesOpen}
        onClose={() => setBulkStatesOpen(false)}
        selectedProcessors={bulkStatesProcessors}
        onSaved={fetchDashboardData}
      />

      <ConfigAuditModal
        open={configAuditOpen}
        onOpenChange={setConfigAuditOpen}
        team={team}
        teamLabel={teamLabel}
      />
    </div>
  );
};

export default AutoAssignmentDashboard;
